import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, KeyRound, Sparkles } from "lucide-react";
import Seo from "@/lib/seo";
import api from "@/lib/api";

export default function MonProjetPage() {
  const [ref, setRef] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!ref.trim() || !email.trim()) { toast.error("Renseignez votre numéro de projet et votre email."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/projects/lookup", { ref: ref.trim(), email: email.trim() });
      nav(`/projet/${data.access_token}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Aucun projet trouvé avec ces informations.";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-[100px] md:pt-[140px] pb-24">
      <Seo title="Mon projet — Accès à votre espace client | RAMA DÉCO" description="Accédez au suivi de votre chantier RAMA DÉCO avec votre numéro de projet." path="/mon-projet" noindex />
      <div className="wrap max-w-5xl grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div className="font-mono-caption text-bordeaux-700 mb-3">Espace projet</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight text-anthracite leading-[1.05]">
            Retrouvez votre projet <span className="italic text-bordeaux-700">en un instant.</span>
          </h1>
          <p className="mt-6 text-ink-soft text-base md:text-lg leading-relaxed">
            Renseignez votre numéro de projet (par exemple <b className="text-anthracite">RAMA-12345</b>) et l'email
            que vous avez utilisé lors de la prise de rendez-vous. Vous accéderez à toutes les informations
            de votre chantier : estimation, avancement, rendez-vous, documents.
          </p>

          <div className="mt-10 space-y-4">
            <div className="flex gap-3 items-start p-4 bg-ivory rounded-rama-sm border border-black/5">
              <Sparkles size={18} className="text-bordeaux-700 mt-1 flex-shrink-0" />
              <div>
                <div className="font-display text-lg text-anthracite">Aucun projet encore ?</div>
                <div className="text-sm text-ink-soft mt-1">Créez votre projet en décrivant simplement vos travaux, en 5 minutes.</div>
                <Link to="/estimation" className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-bordeaux-700 link-underline">
                  Estimer mon projet <ArrowRight size={14} />
                </Link>
              </div>
            </div>
            <div className="flex gap-3 items-start p-4 bg-ivory rounded-rama-sm border border-black/5">
              <KeyRound size={18} className="text-bordeaux-700 mt-1 flex-shrink-0" />
              <div>
                <div className="font-display text-lg text-anthracite">Numéro perdu ?</div>
                <div className="text-sm text-ink-soft mt-1">
                  Appelez-nous au <a href="tel:0627443230" className="text-anthracite font-semibold link-underline">06 27 44 32 30</a>, nous vous retrouverons.
                </div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-black/10 rounded-rama-md p-6 md:p-10 shadow-[0_30px_80px_-30px_rgba(30,30,30,0.15)] h-max">
          <div className="font-mono-caption text-ink-soft mb-4">Accéder à mon projet</div>
          <h2 className="font-display text-2xl md:text-3xl text-anthracite">Deux informations, c'est tout.</h2>

          <div className="mt-8 space-y-5">
            <div>
              <label className="text-xs font-mono-caption text-ink-soft block mb-2">Numéro de projet</label>
              <input
                required
                data-testid="lookup-ref"
                placeholder="RAMA-XXXXX"
                value={ref}
                onChange={(e) => setRef(e.target.value.toUpperCase())}
                className="w-full py-3 px-4 bg-ivory border border-black/10 rounded-rama-sm focus:border-bordeaux-700 outline-none text-anthracite font-mono tracking-widest"
              />
            </div>
            <div>
              <label className="text-xs font-mono-caption text-ink-soft block mb-2">Email utilisé lors de la prise de rendez-vous</label>
              <input
                required
                type="email"
                inputMode="email"
                data-testid="lookup-email"
                placeholder="votre@email.fr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full py-3 px-4 bg-ivory border border-black/10 rounded-rama-sm focus:border-bordeaux-700 outline-none text-anthracite"
              />
            </div>
          </div>

          <button type="submit" data-testid="lookup-submit" disabled={loading} className="btn-primary w-full justify-center mt-8 disabled:opacity-60">
            {loading ? "Recherche…" : "Accéder à mon projet"} {!loading && <ArrowRight size={16} />}
          </button>

          <p className="text-[0.7rem] text-ink-soft mt-5 leading-relaxed">
            Vos données sont utilisées uniquement pour vous permettre d'accéder à votre projet.
            Aucun mot de passe n'est nécessaire pour cette version.
          </p>
        </form>
      </div>
    </div>
  );
}
